import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FaCalendarAlt, FaChevronLeft, FaChevronRight, FaPhone, FaStar } from "react-icons/fa";
import AppHeader from "../common/AppHeader";
import BottomNav from "../common/BottomNav";
import Spinner from "../common/Spinner";
import CategoryIcon from "../common/CategoryIcon";
import { getEventServiceByIdApi } from "../../services/eventService";
import { getServiceIcon, getServiceLabel } from "../utils/serviceCategories";
import { useTranslation } from "react-i18next";
import styles from "../css/EventServiceDetails.module.css";

export default function EventServiceDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { esId } = useParams();

  const [service, setService] = useState(location.state?.service || null);
  const [loading, setLoading] = useState(!location.state?.service);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    let isMounted = true;

    async function loadServiceById() {
      if (location.state?.service || !esId) return;

      setLoading(true);
      try {
        const res = await getEventServiceByIdApi(esId);
        if (isMounted) {
          setService(res?.data || res?.responseData || null);
        }
      } catch {
        if (isMounted) {
          setService(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadServiceById();

    return () => {
      isMounted = false;
    };
  }, [esId, location.state?.service]);

  const images = useMemo(() => {
    if (!service?.serviceImageUrls) return [];
    return service.serviceImageUrls
      .split(",")
      .map((url) => url.trim())
      .filter(Boolean);
  }, [service?.serviceImageUrls]);

  const ratings = useMemo(() => {
    if (!Array.isArray(service?.ratings)) return [];
    return service.ratings;
  }, [service?.ratings]);

  const averageRating = useMemo(() => {
    const values = ratings
      .map((item) => Number(item?.ratingValue))
      .filter((value) => Number.isFinite(value) && value > 0);

    if (values.length === 0) return 0;
    return values.reduce((sum, value) => sum + value, 0) / values.length;
  }, [ratings]);

  // Admin comment on an individual rating/review
  const getAdminComment = (review) => (
    review?.adminComment
    || review?.admin_comment
    || review?.adminReview
    || review?.adminRemarks
    || review?.moderatorComment
    || ""
  );

  // Admin comment on the listing itself (set when rejected/deactivated)
  const getServiceAdminComment = (svc) => {
    const keys = [
      "comment",
      "adminComment",
      "rejectionReason",
      "rejectReason",
      "deactivationReason",
      "deactivateReason",
      "reason",
    ];
    for (const key of keys) {
      const val = svc?.[key];
      if (typeof val === "string" && val.trim()) return val.trim();
    }
    return "";
  };

  const handlePrevImage = () => {
    if (images.length <= 1) return;
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNextImage = () => {
    if (images.length <= 1) return;
    setCurrentImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  const ratingCount = ratings.length;
  const minPrice = Number(service?.minPrice);
  const maxPrice = Number(service?.maxPrice);
  const hasMinPrice = Number.isFinite(minPrice) && minPrice > 0;
  const hasMaxPrice = Number.isFinite(maxPrice) && maxPrice > 0;
  const priceDisplay = hasMinPrice
    ? (hasMaxPrice && maxPrice !== minPrice
        ? `INR ${minPrice} - ${maxPrice}`
        : `INR ${minPrice}+`)
    : "-";

  return (
    <div className={styles.pageContainer}>
      <AppHeader
        title={t("ManageEventServices.view_service", "Service Details")}
        showBack={true}
      />

      <div className={styles.contentWrapper}>
        {loading ? (
          <div className={styles.loadingContainer}>
            <Spinner text={t("Common.loading", "Loading...")} />
          </div>
        ) : !service ? (
          <div className={styles.emptyCard}>
            <FaCalendarAlt size={34} color="#94a3b8" />
            <div className={styles.emptyTitle}>{t("Common.no_data", "Service not found")}</div>
            <button type="button" className={styles.backBtn} onClick={() => navigate("/manage-event-services")}>
              {t("Common.back", "Back")}
            </button>
          </div>
        ) : (
          <div className={styles.card}>
            <div className={styles.imageContainer}>
              {images.length > 0 ? (
                <>
                  <img
                    src={images[currentImageIndex]}
                    alt={service.businessName || "Service"}
                    className={styles.image}
                  />
                  {images.length > 1 && (
                    <>
                      <button className={styles.imageNavLeft} onClick={handlePrevImage}>
                        <FaChevronLeft />
                      </button>
                      <button className={styles.imageNavRight} onClick={handleNextImage}>
                        <FaChevronRight />
                      </button>
                    </>
                  )}
                </>
              ) : (
                <FaCalendarAlt size={56} color="#94a3b8" />
              )}
            </div>

            <div className={styles.body}>
              <div className={styles.title}>{service.businessName || t("ManageEventServices.unnamed_service", "Unnamed Service")}</div>
              <div className={styles.owner}>{service.ownerName || "-"}</div>

              <div className={styles.ratingSummary}>
                {ratingCount > 0 ? (
                  <>
                    <span className={styles.stars}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <FaStar
                          key={star}
                          className={star <= Math.round(averageRating) ? styles.starFilled : styles.starEmpty}
                        />
                      ))}
                    </span>
                    <span className={styles.ratingValue}>{averageRating.toFixed(1)}</span>
                    <span className={styles.ratingCount}>({ratingCount})</span>
                  </>
                ) : (
                  <span className={styles.noRating}>{t("ManageEventServices.no_ratings", "No ratings yet")}</span>
                )}
              </div>

              <div className={styles.infoLine}><FaPhone className={styles.phoneIcon} /> {service.primaryContact || service.contactNumber || "-"}</div>
              <div className={styles.infoLine}><strong>{t("ManageEventServices.address", "Address")}:</strong> {[service.address, service.city, service.state].filter(Boolean).join(", ") || "-"}</div>
              <div className={styles.infoLine}><strong>{t("ManageEventServices.price_range", "Price Range")}:</strong> {priceDisplay}</div>
              <div className={styles.infoLine}><strong>{t("ManageEventServices.experience", "Experience")}:</strong> {service.experience ? `${service.experience} ${t("ManageEventServices.years", "Years")}` : "-"}</div>
              <div className={styles.infoLine}><strong>{t("ManageEventServices.status", "Status")}:</strong> {service.status || "-"} | <strong>{t("ManageEventServices.approval", "Approval")}:</strong> {service.approvalStatus || "-"}</div>
              <div className={styles.infoLine}><strong>Service ID:</strong> {service.esId}</div>

              {(String(service.status ?? "").toLowerCase() === "inactive" ||
                String(service.approvalStatus ?? "").toLowerCase() === "rejected") &&
                getServiceAdminComment(service) && (
                  <div className={styles.adminBanner}>
                    <div className={styles.adminBannerTitle}>{t("ManageEventServices.admin_comment", "Admin comment")}</div>
                    <div className={styles.adminBannerText}>{getServiceAdminComment(service)}</div>
                  </div>
                )}

              {service.subCategory && (
                <div className={styles.categoryWrap}>
                  {service.subCategory.split(",").map((cat) => cat.trim()).filter(Boolean).map((cat, idx) => (
                    <span key={`${service.esId}-cat-${idx}`} className={styles.categoryBadge}>
                      <CategoryIcon icon={getServiceIcon(cat)} size={14} style={{ marginRight: "4px" }} />
                      {getServiceLabel(cat) || cat}
                    </span>
                  ))}
                </div>
              )}

              {service.serviceDescription && (
                <div className={styles.descriptionBlock}>
                  <div className={styles.sectionTitle}>{t("ManageEventServices.description", "Description")}</div>
                  <div className={styles.descriptionText}>{service.serviceDescription}</div>
                </div>
              )}

              <div className={styles.reviewsBlock}>
                <div className={styles.sectionTitle}>{t("ManageEventServices.reviews", "Reviews")}</div>
                {ratings.length > 0 ? (
                  <div className={styles.reviewList}>
                    {ratings.map((review, index) => {
                      const adminComment = getAdminComment(review);
                      const reviewKey = review.id || `${review.userId || "u"}-${review.createdAt || index}`;

                      return (
                        <div key={reviewKey} className={styles.reviewItem}>
                          <div className={styles.reviewHeader}>
                            <span className={styles.reviewUser}>{review.userName || t("ManageEventServices.anonymous", "Anonymous")}</span>
                            <span className={styles.reviewStars}>
                              {[1, 2, 3, 4, 5].map((star) => (
                                <FaStar
                                  key={`${reviewKey}-${star}`}
                                  className={star <= Number(review.ratingValue || 0) ? styles.starFilled : styles.starEmpty}
                                />
                              ))}
                            </span>
                          </div>

                          <div className={styles.reviewComment}>{review.comment || t("ManageEventServices.no_comment", "No comment")}</div>

                          {adminComment && (
                            <div className={styles.adminComment}>
                              <span className={styles.adminLabel}>{t("ManageEventServices.admin_comment", "Admin comment")}:</span>
                              <span>{adminComment}</span>
                            </div>
                          )}

                          <div className={styles.reviewMeta}>
                            {review.createdAt
                              ? new Date(review.createdAt).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" })
                              : ""}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className={styles.noRating}>{t("ManageEventServices.no_reviews", "No reviews yet")}</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
